import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoles, TRoutes } from '../../../../shared/const'
import { formatVienna } from '../../../../shared/datetime'

import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'

import TeacherCourses from '../../teacher/Courses'

import styles from './style.module.scss'

/*
 * Der Menüpunkt „Kurse": Kurse von TalentsLounge, eingebettet in die
 * Anwendung (seit 01.11.2026). Urkunde und Gutschrift stellt weiter Junior
 * aus — der Kurs läuft auf TalentsLounge und meldet den Abschluss zurück.
 *
 * Schüler:innen sehen die Kurse ihrer Unternehmensform. Lehrkräfte sehen
 * darunter den Stand ihrer Junior Companies und — nur sie, weil sie über den
 * Unterricht entscheiden — zwei Angebote von TalentsLounge für ihre Klassen.
 */

const TL = 'https://talentslounge.com'
// Herkunftskennung: TalentsLounge zählt, wie viele Lehrkräfte über Junior kommen.
const VON = 'von=junior'
const TL_ANGEBOTE = [
	{
		key: 'tipp',
		title: 'Typing licence',
		text: 'Ten-finger typing in short rounds — free on TalentsLounge, for your whole class.',
		label: 'Free for your classes',
		href: `${TL}/angebot/tastatur-tippkurs?${VON}`
	},
	{
		key: 'marktplatz',
		title: 'More courses',
		text: 'Courses for your lessons, orderable via the Marktplatz Lern-Apps of the BMB.',
		label: 'TalentsLounge',
		href: `${TL}/marktplatz?${VON}`
	}
]

function KursKachel({ kurs, onOpen, vorschau }) {
	const { t } = useTranslation()
	const fertig = kurs.progress?.completed
	return (
		<button type="button" className={styles.card} onClick={onOpen} disabled={vorschau}>
			<span className={`${styles.badge} ${fertig ? styles.badgeDone : ''}`}>
				{fertig ? t('Completed') : t('Recommended')}
			</span>
			<span className={styles.cardTitle}>{kurs.title}</span>
			{kurs.subtitle && <span className={styles.cardSub}>{kurs.subtitle}</span>}
			<span className={styles.cardText}>{t('Certificate and credit for your Junior Company')}</span>
			<span className={styles.cardFoot}>
				{fertig
					? t('Completed on {{date}}', { date: formatVienna(kurs.progress.completedAt, 'dd.MM.yyyy') })
					: vorschau
					? t('Students start the course themselves in the Junior app. Here you see who has completed it.')
					: t('Open course')}
			</span>
		</button>
	)
}

KursKachel.propTypes = {
	kurs: PropTypes.object.isRequired,
	onOpen: PropTypes.func.isRequired,
	vorschau: PropTypes.bool
}

KursKachel.defaultProps = { vorschau: false }

export default function TlCourses({ role }) {
	const { t } = useTranslation()
	const history = useHistory()
	const [stand, setStand] = useState(null)
	const lehrkraft = role === TRoles.TEACHER

	useEffect(() => {
		http.get(TApi.TL_COURSES, { disableToast: true })
			.then(({ data }) => setStand(data))
			.catch(() => setStand({ enabled: false, courses: [] }))
	}, [])

	const kurse = stand?.courses || []

	return (
		<div className={styles.root}>
			<MiniHeader title={t('Courses')} bg="#f8faff" />

			{!stand && <Loading />}

			{stand && kurse.length === 0 && !lehrkraft && (
				<p className={styles.empty}>
					{stand.enabled ? t('There are no courses for you at the moment.') : t('Courses are switched off.')}
				</p>
			)}

			{kurse.length > 0 && (
				<div className={styles.grid}>
					{kurse.map(kurs => (
						<KursKachel
							key={kurs.id}
							kurs={kurs}
							vorschau={stand.mode !== 'LEARN'}
							onOpen={() => history.push(TRoutes.TL_COURSE(kurs.id))}
						/>
					))}
				</div>
			)}

			{lehrkraft && (
				<>
					<h2 className={styles.section}>{t('Free for your classes')}</h2>
					<div className={styles.grid}>
						{TL_ANGEBOTE.map(angebot => (
							<a
								key={angebot.key}
								className={`${styles.card} ${styles.cardExtern}`}
								href={angebot.href}
								target="_blank"
								rel="noopener noreferrer"
								title={t('Opens TalentsLounge in a new tab')}
							>
								<span className={`${styles.badge} ${styles.badgeTl}`}>{t(angebot.label)}</span>
								<span className={styles.cardTitle}>{t(angebot.title)}</span>
								<span className={styles.cardText}>{t(angebot.text)}</span>
								<span className={styles.cardFoot}>{t('Opens TalentsLounge in a new tab')} ↗</span>
							</a>
						))}
					</div>
					<TeacherCourses eingebettet />
				</>
			)}
		</div>
	)
}

TlCourses.propTypes = { role: PropTypes.string.isRequired }
